import styled from "@emotion/styled"
import { graphql, navigate, StaticQuery } from "gatsby"
import React, { useContext, useRef, useEffect, useCallback } from "react"
import { Flex, Input } from "theme-ui"
import { InterfaceContext } from "../../context/interface-context"
import StoreContext from "../../context/store-context"
import Text from "../base/text/text"
import useNewsletterSignup from "../../hooks/use-newsletter-signup"

const SideNavigationContainer = styled(Flex)`
  background-color: ${(props) => props.theme.colors.white};
  flex-direction: column;
  position: fixed;
  z-index: 1021;
  transition: transform 0.5s ease-out;
  transform: translateX(-100%);
  top: ${({ theme }) => theme.height[0]}px;
  overflow-y: auto;
  height: calc(100% - ${({ theme }) => theme.height[0]}px);
  max-width: 500px;
  width: 100%;

  ${(props) =>
    !props.hideShippingBar &&
    props.freeShippingLimit &&
    `
      top: 85px !important;
      height: calc(100% - 85px) !important;
    `}

  ${(props) =>
    props.visible &&
    `
      transform: translateX(0);
    `}
`

const MainNavigation = styled(Flex)`
  flex-direction: column;
`

const MainNavigationItem = styled(Flex)`
  border-bottom: 1px solid #30483e1a;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  padding: 15px;
  cursor: pointer;
`

const MiddleNavigation = styled(Flex)`
  flex-direction: column;
  margin-top: 71px;
  gap: 5px;
`

const MiddleNavigationItem = styled.text`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  margin-inline: 15px;
  cursor: pointer;

  &.region {
    align-self: flex-start;
    border-bottom: 1px solid ${(props) => props.theme.colors.darkerGreen};
    position: relative;
    min-width: 106px;
    cursor: pointer;
    margin-top: 14px;

    &::before {
      content: "";
      position: absolute;
      right: 0;
      bottom: 0;
      width: 1px;
      height: 6px;
      background-color: ${(props) => props.theme.colors.darkerGreen};
    }
  }
`

const Footer = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.darkerGreen};
  padding: 18px;
  margin-top: 22px;

  .email-input {
    border-color: #0000004d;
    width: 100%;
    height: 34px;
    font-size: ${({ theme }) => theme.fontSizes[1]}px;
    color: ${(props) => props.theme.colors.black};
    margin-top: 14px;
    margin-bottom: 9px;

    &::placeholder {
      opacity: 1;
    }
  }
`

const FooterText = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${(props) => props.theme.colors.darkerGreen};
  padding-top: 0;
`

const SubscribeButton = styled.button`
  background-color: ${(props) => props.theme.colors.darkerGreen};
  color: ${(props) => props.theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  border-radius: 5px;
  border: none;
  width: 100%;
  height: 34px;
  cursor: pointer;
`

const ProductNav = () => {
  const {
    isProductNavOpen,
    hideDrawers,
    isRegionOpen,
    toggleRegionOpen,
    hideShippingBar,
  } = useContext(InterfaceContext)
  const productNavRef = useRef()

  const { cart, freeShippingLimit } = useContext(StoreContext)

  const { handleEmailChange, handleSignup, signingUp, state } =
    useNewsletterSignup()

  const handleClick = (node) => {
    if (node.slug) {
      hideDrawers()
      navigate(`/${node.slug}`)
    } else if (typeof node === "string") {
      hideDrawers()
      navigate(`/${node}`)
    }
  }

  const handleHideDrawers = useCallback(() => {
    if (!isRegionOpen) {
      hideDrawers()
    }
  }, [hideDrawers, isRegionOpen])

  useEffect(() => {
    if (!isProductNavOpen) {
      return
    }

    const handleOutsideClick = (e) => {
      if (productNavRef.current && !productNavRef.current.contains(e.target)) {
        handleHideDrawers()
      }
    }

    const escFunction = (event) => {
      if (event.key === "Escape") {
        handleHideDrawers()
      }
    }

    document.addEventListener("click", handleOutsideClick, true)
    document.addEventListener("keydown", escFunction, true)

    return () => {
      document.removeEventListener("click", handleOutsideClick, true)
      document.removeEventListener("keydown", escFunction, true)
    }
  }, [handleHideDrawers, isProductNavOpen])

  return (
    <StaticQuery
      query={graphql`
        {
          productNav: contentfulNavigationMenu(name: { eq: "Products" }) {
            items {
              ... on ContentfulCategory {
                name
                slug
                id
              }
            }
            name
            id
          }
        }
      `}
      render={({ productNav }) => (
        <SideNavigationContainer
          ref={productNavRef}
          visible={isProductNavOpen}
          hideShippingBar={hideShippingBar}
          freeShippingLimit={Boolean(freeShippingLimit)}
        >
          <MainNavigation>
            {productNav.items.map((item, index) => (
              <MainNavigationItem key={index} onClick={() => handleClick(item)}>
                {item.name}
              </MainNavigationItem>
            ))}
          </MainNavigation>
          <MiddleNavigation>
            <MiddleNavigationItem onClick={() => handleClick("stories")}>
              Stories
            </MiddleNavigationItem>
            <MiddleNavigationItem onClick={() => handleClick("about")}>
              About
            </MiddleNavigationItem>
            <MiddleNavigationItem
              isHiddenOnDesktop
              onClick={() => toggleRegionOpen()}
              className="region"
            >
              {cart.currencyCode.toUpperCase()}
            </MiddleNavigationItem>
          </MiddleNavigation>
          <Footer>
            <FooterText>
              Sign up for our Newsletter and get
              <br />
              <strong>10% off on your next purchase</strong>
            </FooterText>
            <Input
              className="email-input"
              placeholder="Enter your email"
              onChange={handleEmailChange}
            />
            <SubscribeButton
              onClick={handleSignup}
              disabled={state.signupComplete || signingUp}
            >
              Subscribe and get 10% off
            </SubscribeButton>
          </Footer>
        </SideNavigationContainer>
      )}
    />
  )
}

export default ProductNav
